import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Badge,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import MessageApi from '../../api/MessageApi';
import { ConversationDto } from '../../interfaces/MessageInterfaces';
import { DateTime } from 'luxon';
import './ManagerMessagesView.scss';

interface SearchFilters {
  /** Any part of the client's name — the server matches it against first and last name alike. */
  clientName: string;
  userEmail: string;
  messageManagerSeen: string; // 'any', 'seen', 'unseen'
}

const initialFilters: SearchFilters = {
  clientName: '',
  userEmail: '',
  messageManagerSeen: 'any',
};

const ManagerMessagesView: React.FC = () => {
  const navigate = useNavigate();
  
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [currentFrom, setCurrentFrom] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  

  
  const RESULTS_PER_PAGE = 20;

  const fetchConversations = useCallback(async (from = 0, searchFilters = filters, appendResults = false) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const to = from + RESULTS_PER_PAGE;
      const params: any = {
        from,
        to,
      };
      
      // Add filters if they have values
      // One name box rather than first/last: a first name typed into the last-name box found
      // nothing (Ashley, Aug 18), so the server matches any part of the name against either column.
      if (searchFilters.clientName.trim()) {
        params.User_Name = searchFilters.clientName.trim();
      }
      if (searchFilters.userEmail.trim()) {
        params.User_Email = searchFilters.userEmail.trim();
      }
      if (searchFilters.messageManagerSeen !== 'any') {
        params.Message_ManagerSeen = searchFilters.messageManagerSeen === 'seen';
      }

      const fetchedConversations = await MessageApi.getConversations(params);

      if (appendResults) {
        console.log('appendResults');
        console.log(appendResults);
        setConversations(prev => [...prev, ...fetchedConversations]);
      } else {
        console.log('fetchedConversations');
        console.log(fetchedConversations);
        setConversations(fetchedConversations);
      }

      setHasMoreData(fetchedConversations.length === RESULTS_PER_PAGE);
      setCurrentFrom(from);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  const handleFilterChange = (filterName: keyof SearchFilters, value: string) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleSearch = () => {
    setCurrentFrom(0);
    setHasSearched(true);
    fetchConversations(0, filters, false);
  };

  const handleLoadMore = () => {
    if (hasMoreData && !loadingMore) {
      const nextFrom = currentFrom + RESULTS_PER_PAGE;
      fetchConversations(nextFrom, filters, true);
    }
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentFrom(0);
    setHasMoreData(false);
    setHasSearched(false);
    fetchConversations(0, initialFilters, false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const openConversation = (conversation: ConversationDto) => {
    navigate(`/message?User_guid=${conversation.lastMessage?.User.User_guid}`);
  };



  const formatMessageTime = (timestamp: string | Date) => {
    const dt = DateTime.fromJSDate(new Date(timestamp));
    return dt.toRelative() || dt.toLocaleString();
  };

  const formatLastMessagePreview = (lastMessage: string | null) => {
    if (!lastMessage) return 'No messages';
    const preview = lastMessage.substring(0, 100);
    return preview.length < lastMessage.length ? `${preview}...` : preview;
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Message Conversations
        </Typography>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Users
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Client Name"
              placeholder="Any part of the name"
              value={filters.clientName}
              onChange={(e) => handleFilterChange('clientName', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
            />
            
            <TextField
              label="User Email"
              value={filters.userEmail}
              onChange={(e) => handleFilterChange('userEmail', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 250, flex: 1 }}
            />
            
            <FormControl variant="outlined" size="small" sx={{ minWidth: 120, flex: 1 }}>
              <InputLabel>Seen</InputLabel>
              <Select
                value={filters.messageManagerSeen}
                onChange={(e) => handleFilterChange('messageManagerSeen', e.target.value)}
                label="Seen"
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="seen">Seen</MenuItem>
                <MenuItem value="unseen">Unseen</MenuItem>
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              disabled={loading}
            >
              Search Messages
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Results */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : conversations.length === 0 && hasSearched ? (
          <Alert severity="info">
            No conversations found matching your search criteria.
          </Alert>
        ) : conversations.length === 0 ? (
          <Alert severity="info">
            No message conversations yet.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Last Message</TableCell>
                    <TableCell>Last Activity</TableCell>
                    <TableCell>Unseen</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversations.map((conversation) => (
                    <TableRow 
                      key={conversation.lastMessage?.User.User_guid}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openConversation(conversation)}
                    >
                      <TableCell>
                        <Typography variant="subtitle2">
                          {conversation.lastMessage?.User.User_FirstName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {conversation.lastMessage?.User.User_LastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {/* {conversation.lastMessage?.User.User_Email} */}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" sx={{ 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis', 
                          whiteSpace: 'nowrap' 
                        }}>
                          {formatLastMessagePreview(conversation.lastMessage?.Message.Message_Body || null)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {conversation.lastMessage?.Message.Message_CreatedAt ? formatMessageTime(conversation.lastMessage.Message.Message_CreatedAt) : 'Never'}
                      </TableCell>
                      <TableCell>
                        {conversation.unseenCount > 0 ? (
                          <Badge badgeContent={conversation.unseenCount} color="error">
                            <MessageIcon />
                          </Badge>
                        ) : (
                          <MessageIcon color="disabled" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<MessageIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openConversation(conversation);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Load More Button */}
            {hasMoreData && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}


      </Container>
    </>
  );
};

export default ManagerMessagesView;